import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

export default function usePartnerSubscription() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [services, setServices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [activeServiceKeys, setActiveServiceKeys] = useState([]);
  const [hasSubscription, setHasSubscription] = useState(false);

  const call = useCallback(async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");
    const res = await fetch(`${supabase.supabaseUrl}/functions/v1/createPartnerSubscription`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || "Request failed");
    return json;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await call({ action: "get" });
      setServices(data.services || []);
      setSubscription(data.subscription || null);
      setActiveServiceKeys(data.activeServiceKeys || []);
      setHasSubscription(!!data.hasSubscription);
      return data;
    } catch (e) {
      setError(e.message || "Failed to load subscription");
      return null;
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasAccess = useCallback(
    (serviceKey) => activeServiceKeys.includes(serviceKey),
    [activeServiceKeys],
  );

  const createCheckout = useCallback(
    async (selectedServices) => call({ action: "create", services: selectedServices }),
    [call],
  );

  const updatePlan = useCallback(
    async (selectedServices) => call({ action: "update", services: selectedServices }),
    [call],
  );

  const cancel = useCallback(async () => {
    const data = await call({ action: "cancel" });
    await refresh();
    return data;
  }, [call, refresh]);

  const reactivate = useCallback(async () => {
    const data = await call({ action: "reactivate" });
    await refresh();
    return data;
  }, [call, refresh]);

  return {
    loading,
    error,
    services,
    subscription,
    activeServiceKeys,
    hasSubscription,
    hasAccess,
    refresh,
    createCheckout,
    updatePlan,
    cancel,
    reactivate,
  };
}
